import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FileText, CalendarDays, BookOpen, ArrowRight } from "lucide-react";
import ToastHelper from "@/utils/toastHelper";
import apiClient from "@/services/apiClient";
import { getStatusMeta } from "@/utils/enrolmentStatus";

const formatDate = (value) => value
  ? new Date(value).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" })
  : "-";

export default function StudentEnrolments() {
  const navigate = useNavigate();
  const [enrolments, setEnrolments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const apiBaseUrl = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
        const response = await apiClient.get(`${apiBaseUrl}/StudentEnrollment/MyEnrollments`);
        setEnrolments(response.data?.enrolments || response.data?.Enrolments || []);
      } catch (error) {
        ToastHelper.apiError(error, "Unable to load enrolments.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const openEnrolment = (item) => {
    navigate("/student/enrol-new", { state: { studentBookingId: item.studentBookingId } });
  };

  return (
    <div className="space-y-6" data-testid="student-enrolments">
      <div>
        <div className="text-xs uppercase tracking-wide text-orange-600 font-semibold">Student portal</div>
        <h1 className="mt-1 text-2xl font-display font-extrabold text-[#0A2540]">My enrolments</h1>
        <p className="mt-1 text-sm text-slate-500">View your enrolment status, course and class information, then open the enrolment form.</p>
      </div>

      <div className="wc-card overflow-hidden p-0">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Enrolment</th>
                <th className="px-4 py-3">Course</th>
                <th className="px-4 py-3">Class date</th>
                <th className="px-4 py-3">Progress</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && <tr><td colSpan="6" className="px-4 py-8 text-center text-slate-500">Loading enrolments...</td></tr>}
              {!loading && enrolments.length === 0 && (
                <tr>
                  <td colSpan="6" className="px-4 py-12">
                    <div className="mx-auto max-w-xl text-center" data-testid="no-enrolments">
                      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-orange-50 text-orange-600">
                        <FileText className="h-7 w-7" />
                      </div>
                      <h2 className="mt-4 text-xl font-display font-extrabold text-[#0A2540]">No enrolments yet</h2>
                      <p className="mt-2 text-sm leading-6 text-slate-600">Open My Courses, choose your booked course, and start the enrolment form from that booking.</p>
                      <Link to="/student/my-courses" className="wc-btn-primary mt-5 inline-flex items-center gap-2" data-testid="go-to-my-courses">
                        Go to My Courses <ArrowRight className="h-4 w-4" />
                      </Link>
                    </div>
                  </td>
                </tr>
              )}
              {enrolments.map((item) => {
                const meta = getStatusMeta(Number(item.workFlowStatusNo));
                return (
                  <tr key={item.studentEnrollmentId} className="hover:bg-slate-50/70">
                    <td className="px-4 py-4">
                      <div className="flex items-start gap-3">
                        <div className="rounded-lg bg-orange-50 p-2 text-orange-600"><FileText className="h-4 w-4" /></div>
                        <div>
                          <div className="font-semibold text-[#0A2540]">{item.enrolmentNumber || `Enrolment #${item.studentEnrollmentId}`}</div>
                          <div className="mt-1 text-xs text-slate-500">Booking: {item.bookingNumber || item.studentBookingId}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="font-semibold text-slate-800">{item.courseName || "Course not assigned"}</div>
                      <div className="mt-1 flex items-center gap-1 text-xs text-slate-500"><BookOpen className="h-3.5 w-3.5" />{item.courseCode || "-"} {item.scheduleCode ? `· ${item.scheduleCode}` : ""}</div>
                    </td>
                    <td className="px-4 py-4 text-slate-700"><span className="inline-flex items-center gap-1.5"><CalendarDays className="h-4 w-4 text-slate-400" />{formatDate(item.scheduleDate)}</span></td>
                    <td className="px-4 py-4">
                      <div className="w-28">
                        <div className="mb-1 text-xs text-slate-500">{item.progressPercentage || 0}%</div>
                        <div className="h-1.5 overflow-hidden rounded-full bg-slate-200"><div className="h-full bg-orange-500" style={{ width: `${Math.min(100, item.progressPercentage || 0)}%` }} /></div>
                      </div>
                    </td>
                    <td className="px-4 py-4"><span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${meta.className}`}>{item.workFlowStatusName || meta.label}</span></td>
                    <td className="px-4 py-4 text-right"><button type="button" onClick={() => openEnrolment(item)} className="wc-btn-outline inline-flex items-center gap-1.5 text-xs">Open <ArrowRight className="h-3.5 w-3.5" /></button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
